import { Alert, Anchor, Group, Progress, Stack, Text } from "@mantine/core";
import { Link } from "@tanstack/react-router";

import type { JobStatus } from "../../api/generated/model";
import { ErrorAlert } from "../../platform/ErrorAlert";
import type { HereSave } from "./browser-save";
import { barValue, hereNow, withPercent, type LocalExport, type SaveState } from "./exports";
import { isSettled } from "../../platform/jobs/status";
import { useRender } from "./project";
import classes from "./editor.module.css";
import { readPref } from "../../platform/prefs";
import { libraryIn } from "./exits";

/** Live's words, as its bell says them, then its toast's when the file is saved. */
const STEP_LABEL: Record<JobStatus, string> = {
  queued: "Starting...",
  submitting: "Starting...",
  running: "Exporting...",
  finalizing: "Finalizing...",
  succeeded: "Rendering complete! Your download has started.",
  failed: "The export failed",
  expired: "The export took too long and stopped",
  canceled: "The export was canceled",
};

export interface ExportStatusProps {
  teamId: string;
  brandId: string;
  projectId: string;
  jobId: string;
  /** A Save's render (live's Save to project): kept in the Library, nothing saved here. */
  kept?: boolean;
  /** Where saving its file to the computer stands, once the render succeeded. */
  save: SaveState | undefined;
  onRetry: (url: string) => void;
  /** Where this tab stands on a Save's file it makes, and whether the server took it over (spec fast-save). */
  here?: HereSave | undefined;
  handedOver?: boolean;
}

/**
 * The newest export under the header: its render's status, live through the team stream (and
 * read every 15 s while the stream is down), then live's "Rendering complete! Your download has
 * started." as the browser saves the MP4 (live says it in a toast; ours stay inline, web/AGENTS.md).
 * The render also keeps the MP4 in the brand's Library.
 */
export function ExportStatus(props: ExportStatusProps) {
  const { teamId, brandId, projectId, jobId, save, onRetry, kept = false, here, handedOver = false } = props;
  const render = useRender(teamId, brandId, projectId, jobId);

  if (render.isPending) return null;
  if (render.isError) return <ErrorAlert error={render.error} />;
  const { status, url, error, progress } = render.data;
  // A Save this tab makes the file of says how far it is, as its bell's row does.
  const now = hereNow(here, status);
  if (now !== null && "problem" in now) {
    return (
      <Alert color="red" title="The export failed" className={classes.exportStatus}>
        {now.problem}
      </Alert>
    );
  }
  if (now !== null) {
    return (
      <output className={classes.exportStatus} data-float="progress">
        <Stack gap={4}>
          <Text size="sm" fw={500}>
            {now.words}
          </Text>
          <Progress value={now.value} animated aria-label="Export progress" />
        </Stack>
      </output>
    );
  }
  const library = (
    <Anchor
      size="sm"
      renderRoot={(props) => (
        <Link
          {...props}
          to={libraryIn(readPref("ui"))}
          params={{ teamId, brandId }}
          search={{ kind: "video" }}
        />
      )}
    >
      Open in Library
    </Anchor>
  );
  if (isSettled(status) && status !== "succeeded") {
    return (
      <Alert color="red" title={STEP_LABEL[status]} className={classes.exportStatus}>
        {error?.message ?? "Try the export again."}
      </Alert>
    );
  }
  if (status === "succeeded" && save?.kind === "failed" && url !== null) {
    return (
      <output className={classes.exportStatus}>
        <Text size="sm" role="alert">
          The MP4 is ready, but it was not saved to this computer: {save.problem}{" "}
          <Anchor component="button" type="button" size="sm" onClick={() => onRetry(url)}>
            Try again
          </Anchor>
          {" · "}
          <Anchor href={url} target="_blank" rel="noopener noreferrer" size="sm">
            Open it in a new tab
          </Anchor>
          {" · "}
          {library}
        </Text>
      </output>
    );
  }
  const done = status === "succeeded";
  // A Save's words, live's: its bell's while it renders, then its toast's.
  const words = kept
    ? done
      ? "Video saved successfully!"
      : status === "running"
        ? "Saving Video..."
        : STEP_LABEL[status]
    : STEP_LABEL[status];
  const label = `${withPercent(words, status, progress)}${handedOver && !done ? " · on the server" : ""}`;
  return (
    <output className={classes.exportStatus} data-float={done ? "settled" : "progress"}>
      <Stack gap={4}>
        <Group gap={8} justify="space-between">
          <Text size="sm" fw={500}>
            {label}
          </Text>
          {done ? library : null}
        </Group>
        {done ? null : (
          <Progress value={barValue(status, progress)} animated aria-label="Export progress" />
        )}
      </Stack>
    </output>
  );
}

/**
 * The newest export made in this browser, under the header: "Exporting..." with its frames as the
 * bar, then live's "Rendering complete! Your download has started." as the browser saves the
 * MP4. It stays in this browser: unlike the server's render, nothing goes to the Library (live's
 * Download keeps nothing either).
 */
export function LocalExportStatus({ task }: { task: LocalExport }) {
  if (task.state === "failed") {
    return (
      <Alert color="red" title="The export failed" className={classes.exportStatus}>
        {task.problem ?? "Try the export again."}
      </Alert>
    );
  }
  const done = task.state === "saved";
  return (
    <output className={classes.exportStatus} data-float={done ? "settled" : "progress"}>
      <Stack gap={4}>
        <Text size="sm" fw={500}>
          {done ? STEP_LABEL.succeeded : STEP_LABEL.running}
        </Text>
        {done ? null : (
          <Progress
            value={task.frames === 0 ? 0 : (task.done / task.frames) * 100}
            aria-label="Export progress"
          />
        )}
      </Stack>
    </output>
  );
}
